import { TableView1 } from "..";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import * as PropsType from "prop-types";
import { energyStatementKey, indexedDbGetOnePageData } from "@/utils";
import { yearColumnsTariff } from "@/utils/export/energyStatement";

type TariffYearStatementProps = {
  onChangeEnergyStatement?: (obj: Record<string, unknown>) => void;
  fileNameForExport?: string;
  excelData?: unknown[];
  meterType: string;
  year: number;
  loading?: boolean;
  infoText?: string;
  isClimate?: boolean;
};

export const TariffYearStatement = (props: TariffYearStatementProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  // const locale = useLang();
  const columns = useMemo(() => {
    return yearColumnsTariff(gettext, null);
  }, [gettext]);
  let { currentTeam: customer, currentSubsidiary: group } = user || {};

  if (!customer) {
    customer = "all";
  }

  if (!group) {
    group = "All";
  }

  //#region React Hooks (useState)

  const propsDefault = {
    title: gettext("Annual energy consumption (kWh)"),
    rowCount: -1,
  };

  const [localLoading, setLocalLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(propsDefault.rowCount);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
  });
  const [sortModel, setSortModel] = useState([]);

  // Use props directly or calculate with useMemo instead of state
  const infoText = props.infoText || "";
  const fileNameForExport =
    props.fileNameForExport || "energy-overall-statement";

  const title = useMemo(() => {
    if (props.isClimate) {
      return gettext("Annual CO₂e (Kg)");
    }
    return propsDefault.title;
  }, [gettext, props.isClimate, propsDefault.title]);

  const filters = useMemo(
    () => ({
      meterType: props.meterType,
      customer: customer,
      address: "all",
      group: group,
      year: props.year,
      isClimate: props.isClimate,
    }),
    [customer, group, props.isClimate, props.meterType, props.year],
  );

  const initialState = useMemo(
    () => ({
      pagination: {
        paginationModel,
      },
    }),
    [paginationModel],
  );

  //#endregion React Hooks (useState)

  //#region React Hooks (useEffect)

  useEffect(() => {
    const loadData = async () => {
      setLocalLoading(true);

      if (filters?.meterType) {
        try {
          const key = energyStatementKey("aggregated-tariff", filters);
          const result: any = await indexedDbGetOnePageData(
            "cache",
            key,
            paginationModel,
            sortModel,
            filterModel,
          );

          if (result?.data?.length) {
            setRowCount(result.rowCount);
            setRows(result.data);
          } else if (!props.loading) {
            if (result?.data) {
              setRowCount(result.rowCount);
              setRows(result.data);
            } else {
              setRowCount(0);
              setRows([]);
            }
          }
        } catch {
          // Handle error silently
        } finally {
          setLocalLoading(false);
        }
      } else {
        setLocalLoading(false);
      }
    };

    loadData();
  }, [filterModel, filters, paginationModel, props.loading, sortModel]);

  //#endregion React Hooks (useEffect)

  //#region Handlers

  const handleChangeEnergyStatement = (obj: Record<string, unknown>) => {
    if (typeof props.onChangeEnergyStatement === "function") {
      props.onChangeEnergyStatement(obj);
    }
  };

  //#endregion Handlers

  return (
    <>
      <TableView1
        title={title}
        rows={rows}
        // rows={[]}
        rowCount={rowCount}
        columns={columns}
        loading={props.loading || localLoading}
        initialState={initialState}
        showGridToolbar
        showGridToolbarTariffStatementExport
        // showGridToolbarQuickFilter
        fileNameForExport={fileNameForExport}
        excelData={props.excelData}
        onChangeEnergyStatement={handleChangeEnergyStatement}
        dataMode="server"
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={setSortModel}
        onFilterModelChange={setFilterModel}
        infoText={infoText}
      />
    </>
  );
};

TariffYearStatement.propTypes = {
  onChangeEnergyStatement: PropsType.func,
  fileNameForExport: PropsType.string,
  excelData: PropsType.array,
  // rowCount: PropsType.number,
  meterType: PropsType.string.isRequired,
  year: PropsType.number.isRequired,
  // columns: PropsType.array.isRequired,
  loading: PropsType.bool,
  infoText: PropsType.string,
  isClimate: PropsType.bool,
  // rows: PropsType.array,
};
